import { FilterRequestBase, IFileAttachment, IFilterCriteria } from '.';

export enum TelephonyServiceProvider {
	Unknown = 'Unknown',
	Bandwidth = 'Bandwidth',
	Twilio = 'Twilio',
	Dialpad = 'Dialpad',
}

export enum CampaignRegistrationStatus {
	Accepted = 'Accepted',
	Deactivated = 'Deactivated',
	Declined = 'Declined',
	Failed = 'Failed',
	Pending = 'Pending',
	Verified = 'Verified',
}

export enum TextingBrandStatus {
	Pending = 'Pending',
	Unverified = 'Unverified',
	Verified = 'Verified',
	Failed = 'Failed',
}

export type CompanyAddress = {
	sid?: string;
	supportingDocumentSid?: string;
	street?: string;
	city?: string;
	stateProvince?: string;
	postalCode?: string;
	country?: string;
};

export type BusinessInformation = {
	sid?: string;
	legalBusinessName?: string;
	type: string;
	ein?: string;
	industry?: string;
	stockExchange?: string;
	stockSymbol?: string;
	domain?: string;
	campaignVerifyToken?: string;
	politicalTaxClassification?: PoliticalTaxClassification;
};

export enum PoliticalTaxClassification {
	Nonprofit501c = 'Nonprofit501c',
	Section527 = 'Section527',
}

export enum CampaignConsentType {
	Online = 'Online',
	OfflineForm = 'OfflineForm',
	OfflineVerbal = 'OfflineVerbal',
}

export type CampaignConsent = {
	type?: CampaignConsentType;
	optInScript?: string;
	consentFormUrl?: string;
};

export type DonationInformation = {
	solicitsDonations: boolean;
	paymentProcessor?: string;
};

export type CampaignOptions = {
	consent?: CampaignConsent;
	privacyPolicyUrl?: string;
	termsAndConditionsUrl?: string;
	isDirectLender: boolean;
	includesEmbeddedPhone: boolean;
	embeddedPhoneSample?: string;
	optInFlow?: string;
	optInFlowImage?: IFileAttachment;
	donationInformation?: DonationInformation;
	subUseCase?: 'MARKETING' | 'CUSTOMER_CARE';
};

export type BusinessRepresentative = {
	sid?: string;
	email?: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	mobilePhoneNumber?: string;
	businessTitle?: string;
	jobPosition: string;
};

export type ClientProfile = {
	address?: CompanyAddress;
	businessInformation?: BusinessInformation;
	campaignOptions?: CampaignOptions;
	primaryRepresentative?: BusinessRepresentative;
	secondaryRepresentative?: BusinessRepresentative;
	registrationRequirementsCertified: boolean;
	registrationRequirementsCertifiedAt?: string;
	registrationRequirementsCertifiedBy?: string;
};

type TextingBrand = {
	id?: string;
	status: TextingBrandStatus;
	errorMessage?: string;
	creationDate: string;
};

export type TextingProviderCampaign = {
	id?: string;
	status: CampaignRegistrationStatus;
	provider: TelephonyServiceProvider;
	lastSubmissionDate?: string;
	errorMessage?: string;
};

export enum CampaignComplianceSeverity {
	Unknown = 'Unknown',
	Blocker = 'Blocker',
	Warning = 'Warning',
}

export enum CampaignComplianceSource {
	Unknown = 'Unknown',
	WebOptInForm = 'WebOptInForm',
	PrivacyPolicy = 'PrivacyPolicy',
	Terms = 'Terms',
	Website = 'Website',
	OptInScript = 'OptInScript',
	CampaignDescription = 'CampaignDescription',
}

export enum CampaignComplianceCategory {
	Unknown = 'Unknown',
	RequiredDisclosure = 'RequiredDisclosure',
	ConsentStructure = 'ConsentStructure',
	DocumentAccess = 'DocumentAccess',
	Unverifiable = 'Unverifiable',
}

export enum CampaignComplianceRunTrigger {
	Submission = 'Submission',
	Manual = 'Manual',
}

export enum CampaignComplianceReviewMode {
	Off = 'Off',
	Shadow = 'Shadow',
	Enforce = 'Enforce',
}

export type CampaignComplianceEvidence = {
	source: CampaignComplianceSource;
	detail?: string;
};

export type CampaignComplianceFinding = {
	code: string;
	title?: string;
	/** Single comma-joined string, not an array */
	providerCodes?: string;
	category: CampaignComplianceCategory;
	severity: CampaignComplianceSeverity;
	evidence?: CampaignComplianceEvidence[];
	suggestedFix?: string;
	levitateHelpUrl?: string;
	providerHelpUrl?: string;
	firstSeenRunId?: string;
	lastSeenRunId?: string;
	resolvedAt?: string;
	resolvedByRunId?: string;
};

export type CampaignComplianceRun = {
	id?: string;
	ranAt: string;
	trigger: CampaignComplianceRunTrigger;
	passed: boolean;
	errorMessage?: string;
};

export type CampaignComplianceOverride = {
	runId?: string;
	by?: string;
	at: string;
	reason?: string;
};

export type CampaignComplianceDeclineCode = {
	providerCode?: string;
	levitateCodes?: string[];
	isCategorical: boolean;
	wasPredicted: boolean;
};

export type CampaignComplianceDeclineCorrelation = {
	campaignId?: string;
	declinedAt: string;
	runId?: string;
	unattributed: boolean;
	codes?: CampaignComplianceDeclineCode[];
};

export type CampaignComplianceReview = {
	/** Absent means the response did not resolve the mode — not the same as Off */
	mode?: CampaignComplianceReviewMode;
	passed: boolean;
	isLatestRunOverridden: boolean;
	runs?: CampaignComplianceRun[];
	/** Every finding ever raised, resolved ones included */
	findings?: CampaignComplianceFinding[];
	overrides?: CampaignComplianceOverride[];
	declineCorrelations?: CampaignComplianceDeclineCorrelation[];
};

export type CampaignRegistration = {
	lastModifiedDate: string;
	telephonyProviderCampaignStatuses: Record<TelephonyServiceProvider, CampaignRegistrationStatus>;
	brand?: TextingBrand;
	campaigns?: TextingProviderCampaign[];
	complianceReview?: CampaignComplianceReview;
};

export type TelephonyServiceProviderConfiguration = {
	id?: string;
	clientProfile?: ClientProfile;
	campaignRegistration?: CampaignRegistration;
	forceAllowClientProfileEdit?: boolean;
};

export enum TelephonyConfigurationFilterProperty {
	PhoneNumberScope = 'PhoneNumberScope',
	Capability = 'Capability',
	ConnectionState = 'ConnectionState',
	PhoneNumber = 'PhoneNumber',
}

export type TelephonyConfigurationFilterCriteria = IFilterCriteria<TelephonyConfigurationFilterProperty>;

export type TelephonyConfigurationFilter = FilterRequestBase<
	TelephonyConfigurationFilterProperty,
	TelephonyConfigurationFilterCriteria
>;

export interface TextSettings {
	verifiedCampaignRequiredToText: boolean;
	canRequestTextingPhoneNumber: boolean;
	requiresCampaignRegistration: boolean;
	customerSuccessManagerEmailAddress: string;
}
